import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import { OpenInFull } from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import DynamicTable, { TableStatusBadge } from "../common/New/DynamicTable";
import { expandScheduleOccurrences } from "../../utils/scheduleUi";

const COLORS = ["#4f46e5", "#10b981", "#3b82f6", "#f59e0b"];
const TIMELINE_HOURS = [
  "12 AM",
  "04 AM",
  "08 AM",
  "12 PM",
  "04 PM",
  "08 PM",
  "12 AM",
];

const localeFor = (language) =>
  ({
    en: "en-US",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    hi: "hi-IN",
    ar: "ar-SA",
    zh: "zh-CN",
    kn: "kn-IN",
  })[String(language || "en").split("-")[0]] || "en-US";

const labelOrText = (t, value) =>
  value === "Unassigned" || value === "Unassigned vehicle" || value === "N/A"
    ? t(value)
    : value;

const validDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// Sunday through Saturday.
const startOfWeek = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return addDays(start, -start.getDay());
};

const minutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();
const timelinePosition = (minutes) =>
  `${Math.max(0, Math.min(100, (minutes / 1440) * 100))}%`;
const isSameLocalDate = (date, dateValue) =>
  date && toDateInputValue(date) === dateValue;

const getScheduleOccurrencesForDate = (schedule, selectedDate) => {
  const dayStart = new Date(`${selectedDate}T00:00:00`);
  const dayEnd = new Date(`${selectedDate}T23:59:59.999`);
  const generated = expandScheduleOccurrences(schedule, {
    rangeStart: dayStart,
    rangeEnd: dayEnd,
  });
  const authoritative = [schedule.currentOccurrence, schedule.nextOccurrence]
    .filter((occurrence) => occurrence?.start && occurrence?.end)
    .filter((occurrence) =>
      isSameLocalDate(validDate(occurrence.start), selectedDate),
    );
  const uniqueOccurrences = new Map();

  [...generated, ...authoritative].forEach((occurrence) => {
    const start = validDate(occurrence.start);
    const end = validDate(occurrence.end);
    if (!start || !end || !isSameLocalDate(start, selectedDate)) return;
    uniqueOccurrences.set(`${start.toISOString()}-${end.toISOString()}`, {
      start: start.toISOString(),
      end: end.toISOString(),
    });
  });

  return Array.from(uniqueOccurrences.values());
};

const shiftDateValue = (dateValue, days) => {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const scheduleOccursOnDate = (schedule, dateValue) => {
  const startClock = validDate(schedule?.startTime);
  if (!startClock || !dateValue) return false;
  return (
    expandScheduleOccurrences(
      {
        ...schedule,
        endTime: new Date(startClock.getTime() + 60 * 1000).toISOString(),
      },
      {
        rangeStart: new Date(`${dateValue}T00:00:00`),
        rangeEnd: new Date(`${dateValue}T23:59:59.999`),
      },
    ).length > 0
  );
};

const getOvernightClocks = (schedule) => {
  const startClock = validDate(schedule?.startTime);
  const endClock = validDate(schedule?.endTime);
  if (!startClock || !endClock) return null;
  const startMinutes = minutesOfDay(startClock);
  const endMinutes = minutesOfDay(endClock);
  if (endMinutes <= 0 || endMinutes >= startMinutes) return null;
  return { startMinutes, endMinutes };
};

const getOvernightMorningSlice = (schedule, selectedDate) => {
  const clocks = getOvernightClocks(schedule);
  if (
    !clocks ||
    !scheduleOccursOnDate(schedule, shiftDateValue(selectedDate, -1))
  )
    return null;

  const start = new Date(`${selectedDate}T00:00:00`);
  const end = new Date(start);
  end.setHours(
    Math.floor(clocks.endMinutes / 60),
    clocks.endMinutes % 60,
    0,
    0,
  );
  return {
    start,
    end,
    startMinutes: 0,
    endMinutes: clocks.endMinutes,
    morning: true,
  };
};

const getOvernightEveningSlice = (schedule, selectedDate) => {
  const clocks = getOvernightClocks(schedule);
  if (!clocks || !scheduleOccursOnDate(schedule, selectedDate)) return null;

  const start = new Date(`${selectedDate}T00:00:00`);
  start.setHours(
    Math.floor(clocks.startMinutes / 60),
    clocks.startMinutes % 60,
    0,
    0,
  );
  const end = new Date(`${shiftDateValue(selectedDate, 1)}T00:00:00`);
  end.setHours(
    Math.floor(clocks.endMinutes / 60),
    clocks.endMinutes % 60,
    0,
    0,
  );
  return { start, end, startMinutes: clocks.startMinutes, endMinutes: 1440 };
};

function TimelineContent({ schedules, selectedDate }) {
  const { t, i18n } = useTranslation();
  const timeLocale = localeFor(i18n.language);
  const { vehicles, eventsByVehicle } = useMemo(() => {
    const vehicleMap = new Map();
    const eventMap = new Map();

    schedules.forEach((schedule) => {
      const source = schedule.original || {};
      const assignedVehicles = getAssignedTripVehicles(source, schedule);
      if (!assignedVehicles.length) return;
      const occurrences = getScheduleOccurrencesForDate(source, selectedDate);
      const morningSlice = getOvernightMorningSlice(source, selectedDate);
      const eveningSlice = getOvernightEveningSlice(source, selectedDate);
      const eveningAlreadyShown = eveningSlice
        ? occurrences.some((occurrence) => {
            const start = validDate(occurrence.start);
            return (
              start &&
              isSameLocalDate(start, selectedDate) &&
              Math.abs(minutesOfDay(start) - eveningSlice.startMinutes) < 2
            );
          })
        : false;
      if (!occurrences.length && !morningSlice && !eveningSlice) return;

      assignedVehicles.forEach((vehicle) => {
        const vehicleKey = String(
          vehicle.id || vehicle.device_id || vehicle.name,
        );
        vehicleMap.set(vehicleKey, {
          id: vehicleKey,
          name: vehicle.name || "Unassigned vehicle",
          number:
            vehicle.VehicleNumber ||
            vehicle.vehicleNumber ||
            vehicle.device_id ||
            "--",
        });
        const events = eventMap.get(vehicleKey) || [];
        occurrences.forEach((occurrence) => {
          const start = validDate(occurrence.start);
          const end = validDate(occurrence.end);
          if (!start || !end) return;
          const startMinutes = minutesOfDay(start);
          const endsAfterMidnight =
            toDateInputValue(end) > toDateInputValue(start);
          const endMinutes = endsAfterMidnight
            ? 1440
            : Math.max(startMinutes + 10, minutesOfDay(end));
          const now = new Date();
          const isToday = selectedDate === toDateInputValue(now);
          const scheduleStatus = String(
            source.scheduleStatus || schedule.status || "",
          ).toUpperCase();
          const state =
            scheduleStatus === "CANCELLED"
              ? "cancelled"
              : !isToday
                ? selectedDate < toDateInputValue(now)
                  ? "completed"
                  : "upcoming"
                : now >= end
                  ? "completed"
                  : now >= start
                    ? "running"
                    : "upcoming";
          events.push({
            id: `${schedule.id}-${occurrence.start}`,
            route: schedule.route,
            start,
            end,
            startMinutes,
            endMinutes,
            state,
          });
        });
        if (eveningSlice && !eveningAlreadyShown) {
          const now = new Date();
          const isToday = selectedDate === toDateInputValue(now);
          const scheduleStatus = String(
            source.scheduleStatus || schedule.status || "",
          ).toUpperCase();
          const state =
            scheduleStatus === "CANCELLED"
              ? "cancelled"
              : !isToday
                ? selectedDate < toDateInputValue(now)
                  ? "completed"
                  : "upcoming"
                : now >= eveningSlice.end
                  ? "completed"
                  : now >= eveningSlice.start
                    ? "running"
                    : "upcoming";
          events.push({
            id: `${schedule.id}-evening-${selectedDate}`,
            route: schedule.route,
            start: eveningSlice.start,
            end: eveningSlice.end,
            startMinutes: eveningSlice.startMinutes,
            endMinutes: eveningSlice.endMinutes,
            state,
          });
        }
        if (morningSlice) {
          const now = new Date();
          const isToday = selectedDate === toDateInputValue(now);
          const scheduleStatus = String(
            source.scheduleStatus || schedule.status || "",
          ).toUpperCase();
          const state =
            scheduleStatus === "CANCELLED"
              ? "cancelled"
              : !isToday
                ? selectedDate < toDateInputValue(now)
                  ? "completed"
                  : "upcoming"
                : now >= morningSlice.end
                  ? "completed"
                  : now >= morningSlice.start
                    ? "running"
                    : "upcoming";
          events.push({
            id: `${schedule.id}-morning-${selectedDate}`,
            route: schedule.route,
            start: morningSlice.start,
            end: morningSlice.end,
            startMinutes: morningSlice.startMinutes,
            endMinutes: morningSlice.endMinutes,
            state,
          });
        }
        eventMap.set(vehicleKey, events);
      });
    });

    eventMap.forEach((events) =>
      events.sort((first, second) => first.start - second.start),
    );
    const orderedVehicles = Array.from(vehicleMap.values())
      .filter((vehicle) => (eventMap.get(vehicle.id) || []).length > 0)
      .sort((first, second) => {
        const firstStart = eventMap.get(first.id)?.[0]?.start?.getTime() || 0;
        const secondStart = eventMap.get(second.id)?.[0]?.start?.getTime() || 0;
        if (firstStart !== secondStart) return firstStart - secondStart;
        return first.name.localeCompare(second.name, undefined, {
          numeric: true,
        });
      });
    return { vehicles: orderedVehicles, eventsByVehicle: eventMap };
  }, [schedules, selectedDate]);
  const today = toDateInputValue(new Date());
  const currentMinutes = minutesOfDay(new Date());

  return (
    <div className="schedule-timeline-body">
      <div className="schedule-timeline-hours">
        {TIMELINE_HOURS.map((hour, index) => (
          <span key={`${hour}-${index}`}>{t(hour)}</span>
        ))}
      </div>
      <div className="schedule-timeline-rows">
        {selectedDate === today && vehicles.length > 0 && (
          <div className="schedule-current-time-layer">
            <span
              className="schedule-current-time"
              style={{ left: timelinePosition(currentMinutes) }}
            >
              <b>
                {new Date().toLocaleTimeString(timeLocale, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </b>
            </span>
          </div>
        )}
        {vehicles.map((vehicle) => (
          <div className="schedule-timeline-row" key={vehicle.id}>
            <div>
              <strong>{labelOrText(t, vehicle.name)}</strong>
              <small>{vehicle.number}</small>
            </div>
            <div className="schedule-timeline-track">
              {(eventsByVehicle.get(vehicle.id) || []).map((event) => (
                <span
                  key={event.id}
                  className={`schedule-timeline-trip ${event.state}`}
                  style={{
                    left: timelinePosition(event.startMinutes),
                    width: timelinePosition(
                      event.endMinutes - event.startMinutes,
                    ),
                  }}
                  title={`${event.route}: ${event.start.toLocaleTimeString(timeLocale)} - ${event.end.toLocaleTimeString(timeLocale)}`}
                >
                  {event.route}
                  <small>
                    {event.start.toLocaleTimeString(timeLocale, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    –{" "}
                    {event.end.toLocaleTimeString(timeLocale, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </small>
                </span>
              ))}
            </div>
          </div>
        ))}
        {!vehicles.length && (
          <div className="schedule-empty">
            {t("No trips scheduled for this date")}
          </div>
        )}
      </div>
      <div className="schedule-legend">
        <span className="completed">{t("Completed")}</span>
        <span className="running">{t("Running")}</span>
        <span className="upcoming">{t("Upcoming")}</span>
        <span className="cancelled">{t("Cancelled")}</span>
      </div>
    </div>
  );
}

const TIMELINE_RANGE_OPTIONS = [
  "Today",
  "Tomorrow",
  "Yesterday",
  "Last 7 Days",
  "This Week",
  "Last 14 Days",
  "Last 30 Days",
  "This Month",
];
const SINGLE_DAY_TIMELINE_RANGES = new Set(["Today", "Tomorrow", "Yesterday"]);

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const getTimelineRange = (label, now = new Date()) => {
  const today = startOfDay(now);
  switch (label) {
    case "Tomorrow":
      return { start: addDays(today, 1), end: addDays(today, 2) };
    case "Yesterday":
      return { start: addDays(today, -1), end: today };
    case "This Week": {
      const start = startOfWeek(now);
      return { start, end: addDays(start, 7) };
    }
    case "Last 7 Days":
      return { start: addDays(today, -7), end: today };
    case "Last 14 Days":
      return { start: addDays(today, -14), end: today };
    case "Last 30 Days":
      return { start: addDays(today, -30), end: today };
    case "This Month":
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 1),
      };
    case "Today":
    default:
      return { start: today, end: addDays(today, 1) };
  }
};

const datesInRange = (start, end) => {
  const dates = [];
  for (
    let cursor = startOfDay(start);
    cursor < end;
    cursor = addDays(cursor, 1)
  ) {
    dates.push(new Date(cursor));
  }
  return dates;
};

const tripRunState = (source, schedule, start, end, selectedDate) => {
  const now = new Date();
  const isToday = selectedDate === toDateInputValue(now);
  const scheduleStatus = String(
    source.scheduleStatus || schedule.status || "",
  ).toUpperCase();
  if (scheduleStatus === "CANCELLED") return "cancelled";
  if (!isToday)
    return selectedDate < toDateInputValue(now) ? "completed" : "upcoming";
  if (now >= end) return "completed";
  if (now >= start) return "running";
  return "upcoming";
};

function PeriodTimeline({ schedules, columns, rangeLabel }) {
  const { t, i18n } = useTranslation();
  const timeLocale = localeFor(i18n.language);
  const { vehicles, eventsByVehicle } = useMemo(() => {
    const vehicleMap = new Map();
    const eventMap = new Map();

    columns.forEach((date, columnIndex) => {
      const dateValue = toDateInputValue(date);
      schedules.forEach((schedule) => {
        const source = schedule.original || {};
        const assignedVehicles = getAssignedTripVehicles(source, schedule);
        getScheduleOccurrencesForDate(source, dateValue).forEach(
          (occurrence) => {
            const start = validDate(occurrence.start);
            const end = validDate(occurrence.end);
            if (!start || !end) return;
            assignedVehicles.forEach((vehicle) => {
              const vehicleKey = String(
                vehicle.id || vehicle.device_id || vehicle.name,
              );
              vehicleMap.set(vehicleKey, {
                id: vehicleKey,
                name: vehicle.name || "Unassigned vehicle",
                number:
                  vehicle.VehicleNumber ||
                  vehicle.vehicleNumber ||
                  vehicle.device_id ||
                  "--",
              });
              const events = eventMap.get(vehicleKey) || [];
              events.push({
                id: `${schedule.id}-${vehicleKey}-${dateValue}-${occurrence.start}`,
                route: schedule.route,
                start,
                end,
                columnIndex,
                state: tripRunState(source, schedule, start, end, dateValue),
              });
              eventMap.set(vehicleKey, events);
            });
          },
        );
      });
    });

    const orderedVehicles = Array.from(vehicleMap.values()).sort(
      (first, second) =>
        first.name.localeCompare(second.name, undefined, { numeric: true }),
    );
    return { vehicles: orderedVehicles, eventsByVehicle: eventMap };
  }, [schedules, columns]);

  const dense = columns.length > 10;
  const columnStyle = {
    gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(${dense ? 42 : 0}px, 1fr))`,
    minWidth: dense ? columns.length * 46 : undefined,
  };
  const showRoute = columns.length <= 7;
  const columnLabel = (date) => {
    if (rangeLabel === "This Month")
      return String(date.getDate()).padStart(2, "0");
    if (rangeLabel === "This Week") {
      return date.toLocaleDateString(timeLocale, {
        weekday: "short",
        day: "numeric",
      });
    }
    return date.toLocaleDateString(timeLocale, {
      month: "short",
      day: "2-digit",
    });
  };

  return (
    <div className="schedule-timeline-body">
      <div className="schedule-period-scroll">
        <div
          className={`schedule-period-hours${dense ? " dense" : ""}`}
          style={columnStyle}
        >
          {columns.map((date) => (
            <span key={toDateInputValue(date)}>{columnLabel(date)}</span>
          ))}
        </div>
        <div className="schedule-timeline-rows">
          {vehicles.map((vehicle) => {
            const events = eventsByVehicle.get(vehicle.id) || [];
            return (
              <div className="schedule-timeline-row" key={vehicle.id}>
                <div>
                  <strong>{labelOrText(t, vehicle.name)}</strong>
                  <small>{vehicle.number}</small>
                </div>
                <div
                  className={`schedule-period-track${dense ? " dense" : ""}`}
                  style={columnStyle}
                >
                  {columns.map((date, index) => (
                    <div
                      className="schedule-period-cell"
                      key={toDateInputValue(date)}
                    >
                      {events
                        .filter((event) => event.columnIndex === index)
                        .map((event) => (
                          <span
                            key={event.id}
                            className={`schedule-timeline-trip schedule-period-trip ${event.state}`}
                            title={`${event.route}: ${event.start.toLocaleString(timeLocale)} - ${event.end.toLocaleTimeString(timeLocale)}`}
                          >
                            {showRoute ? event.route : ""}
                            <small>
                              {event.start.toLocaleTimeString(timeLocale, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </small>
                          </span>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {!vehicles.length && (
            <div className="schedule-empty">
              {t("No trips scheduled for this period")}
            </div>
          )}
        </div>
      </div>
      <div className="schedule-legend">
        <span className="completed">{t("Completed")}</span>
        <span className="running">{t("Running")}</span>
        <span className="upcoming">{t("Upcoming")}</span>
        <span className="cancelled">{t("Cancelled")}</span>
      </div>
    </div>
  );
}

function Timeline({ schedules }) {
  const { t } = useTranslation();
  const [rangeLabel, setRangeLabel] = React.useState("Today");
  const [expanded, setExpanded] = React.useState(false);
  const range = getTimelineRange(rangeLabel);
  const columns = datesInRange(range.start, range.end);
  const renderTimeline = () =>
    SINGLE_DAY_TIMELINE_RANGES.has(rangeLabel) ? (
      <TimelineContent
        schedules={schedules}
        selectedDate={toDateInputValue(range.start)}
      />
    ) : (
      <PeriodTimeline
        schedules={schedules}
        columns={columns}
        rangeLabel={rangeLabel}
      />
    );
  const controls = (
    <FormControl size="small" className="schedule-timeline-filter">
      <Select
        value={rangeLabel}
        onChange={(event) => setRangeLabel(event.target.value)}
        aria-label={t("Timeline date range")}
      >
        {TIMELINE_RANGE_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {t(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
  return (
    <div className="schedule-panel schedule-timeline-panel">
      <div className="schedule-panel-heading">
        <h3>{t("Trips Timeline")}</h3>
        <div className="schedule-timeline-actions">
          {controls}
          <IconButton
            size="small"
            onClick={() => setExpanded(true)}
            aria-label={t("Expand trips timeline")}
          >
            <OpenInFull sx={{ fontSize: 14 }} />
          </IconButton>
        </div>
      </div>
      {renderTimeline()}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth="xl"
        fullWidth
      >
        <div className="schedule-timeline-modal">
          <div className="schedule-panel-heading">
            <h3>{t("Trips Timeline")}</h3>
            <div className="schedule-timeline-actions">
              {controls}
              <button type="button" onClick={() => setExpanded(false)}>
                {t("Close")}
              </button>
            </div>
          </div>
          {renderTimeline()}
        </div>
      </Dialog>
    </div>
  );
}

function Upcoming({ schedules }) {
  const { t, i18n } = useTranslation();
  const upcomingTrips = useMemo(() => {
    const now = new Date();
    const rangeEnd = new Date(now);
    rangeEnd.setDate(rangeEnd.getDate() + 14);

    return schedules
      .flatMap((schedule) =>
        expandScheduleOccurrences(schedule.original || {}, {
          rangeStart: now,
          rangeEnd,
        }).map((occurrence) => ({
          id: `${schedule.id}-${occurrence.start}`,
          vehicle: schedule.vehicle,
          route: schedule.route,
          name: schedule.name,
          start: validDate(occurrence.start),
        })),
      )
      .filter((trip) => trip.start && trip.start >= now)
      .sort((first, second) => first.start - second.start)
      .slice(0, 7);
  }, [schedules]);

  return (
    <div className="schedule-panel schedule-upcoming-panel">
      <div className="schedule-panel-heading">
        <h3>{t("Upcoming Trips")}</h3>
      </div>
      <div className="schedule-upcoming-list">
        {upcomingTrips.map((item, index) => (
          <div className="schedule-upcoming-item" key={item.id || index}>
            <i />
            <div>
              <strong>{labelOrText(t, item.vehicle)}</strong>
              <small>
                {item.route} · {item.name}
              </small>
            </div>
            <b>
              {item.start.toLocaleDateString(localeFor(i18n.language), {
                month: "short",
                day: "numeric",
              })}
              <br />
              {item.start.toLocaleTimeString(localeFor(i18n.language), {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
          </div>
        ))}
        {!upcomingTrips.length && (
          <div className="schedule-empty">{t("No upcoming trips")}</div>
        )}
      </div>
    </div>
  );
}

function TripsStartedByHour({ schedules }) {
  const { t } = useTranslation();
  const [dayOffset, setDayOffset] = React.useState(0);
  const { buckets, totalTrips } = useMemo(() => {
    const selectedDate = new Date();
    selectedDate.setDate(selectedDate.getDate() + dayOffset);
    const selectedDateValue = toDateInputValue(selectedDate);
    const tripBuckets = Array.from({ length: 8 }, (_, index) => ({
      name: `${index * 3}-${(index + 1) * 3}`,
      trips: 0,
    }));

    schedules.forEach((schedule) => {
      const source = schedule.original || {};
      const vehicleCount =
        Array.isArray(source.vehicleSchedule) && source.vehicleSchedule.length
          ? source.vehicleSchedule.length
          : 1;
      getScheduleOccurrencesForDate(source, selectedDateValue).forEach(
        (occurrence) => {
          const start = validDate(occurrence.start);
          if (!start) return;
          const bucketIndex = Math.min(7, Math.floor(start.getHours() / 3));
          tripBuckets[bucketIndex].trips += vehicleCount;
        },
      );
    });

    return {
      buckets: tripBuckets,
      totalTrips: tripBuckets.reduce(
        (total, bucket) => total + bucket.trips,
        0,
      ),
    };
  }, [dayOffset, schedules]);

  return (
    <div className="schedule-panel trips-hour-panel">
      <div className="schedule-panel-heading">
        <h3>{t("Trips Started by Hour")}</h3>
        <select
          value={dayOffset}
          onChange={(event) => setDayOffset(Number(event.target.value))}
          aria-label={t("Trips chart date")}
        >
          <option value={0}>{t("Today")}</option>
          <option value={-1}>{t("Yesterday")}</option>
        </select>
      </div>
      <div className="schedule-chart">
        {totalTrips === 0 ? (
          <div className="schedule-chart-empty">{t("No data")}</div>
        ) : (
          <ResponsiveContainer>
            <BarChart
              data={buckets}
              margin={{ top: 8, right: 4, left: -12, bottom: 16 }}
            >
              <XAxis
                dataKey="name"
                tick={{ fontSize: 8 }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: t("Time (24 Hour)"),
                  position: "insideBottom",
                  offset: -10,
                  fontSize: 8,
                }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 8 }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: t("Trips"),
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 8,
                }}
              />
              <Tooltip />
              <Bar
                dataKey="trips"
                name={t("Trips")}
                fill="#3b82f6"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const getAssignedVehicleCount = (schedule) =>
  Array.isArray(schedule?.vehicleSchedule) && schedule.vehicleSchedule.length
    ? schedule.vehicleSchedule.length
    : 1;

function TripsStatusChart({ schedules }) {
  const { t } = useTranslation();
  const [dayOffset, setDayOffset] = React.useState(0);
  const statuses = useMemo(() => {
    const selectedDate = new Date();
    selectedDate.setDate(selectedDate.getDate() + dayOffset);
    const selectedDateValue = toDateInputValue(selectedDate);
    const now = new Date();
    const counts = { Completed: 0, Running: 0, Upcoming: 0, Cancelled: 0 };

    schedules.forEach((schedule) => {
      const source = schedule.original || {};
      const multiplier = getAssignedVehicleCount(source);
      getScheduleOccurrencesForDate(source, selectedDateValue).forEach(
        (occurrence) => {
          const start = validDate(occurrence.start);
          const end = validDate(occurrence.end);
          if (!start || !end) return;
          if (String(source.scheduleStatus || "").toUpperCase() === "CANCELLED")
            counts.Cancelled += multiplier;
          else if (selectedDateValue < toDateInputValue(now) || end <= now)
            counts.Completed += multiplier;
          else if (selectedDateValue > toDateInputValue(now) || start > now)
            counts.Upcoming += multiplier;
          else counts.Running += multiplier;
        },
      );
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [dayOffset, schedules]);
  const total = statuses.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="schedule-panel trips-status-panel">
      <div className="schedule-panel-heading">
        <h3>{t("Trips Status")}</h3>
        <select
          value={dayOffset}
          onChange={(event) => setDayOffset(Number(event.target.value))}
          aria-label={t("Trips chart date")}
        >
          <option value={0}>{t("Today")}</option>
          <option value={-1}>{t("Yesterday")}</option>
        </select>
      </div>
      {total === 0 ? (
        <div className="schedule-chart schedule-chart-empty">
          {t("No data")}
        </div>
      ) : (
        <div className="schedule-status-content">
          <div className="schedule-chart status-chart">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={statuses}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={66}
                  paddingAngle={1}
                >
                  {statuses.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, t(name)]} />
              </PieChart>
            </ResponsiveContainer>
            <strong>
              {total}
              <small>{t("Total")}</small>
            </strong>
          </div>
          <div className="schedule-status-legend">
            {statuses.map((item, index) => (
              <div key={item.name}>
                <i style={{ background: COLORS[index] }} />
                <span>{t(item.name)}</span>
                <b>{item.value}</b>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const formatShortDate = (date) =>
  date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
    .replaceAll("/", "-");

function WeeklyTripsTrend({ schedules }) {
  const { t, i18n } = useTranslation();
  const [weekOffset, setWeekOffset] = React.useState(0);
  const { data, label } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay() + weekOffset * 7);
    const fullWeekEnd = new Date(start);
    fullWeekEnd.setDate(fullWeekEnd.getDate() + 6);
    const end =
      weekOffset === 0 && now < fullWeekEnd ? new Date(now) : fullWeekEnd;
    end.setHours(23, 59, 59, 999);
    const points = [];
    for (
      const date = new Date(start);
      date <= end;
      date.setDate(date.getDate() + 1)
    ) {
      const dateValue = toDateInputValue(date);
      let trips = 0;
      schedules.forEach((schedule) => {
        const source = schedule.original || {};
        trips +=
          getScheduleOccurrencesForDate(source, dateValue).length *
          getAssignedVehicleCount(source);
      });
      points.push({
        name: date.toLocaleDateString(localeFor(i18n.language), {
          weekday: "short",
        }),
        trips,
        date: dateValue,
      });
    }
    const prefixKey =
      weekOffset === 0
        ? "This Week"
        : weekOffset < 0
          ? "Last Week"
          : "Next Week";
    return {
      data: points,
      label: `${t(prefixKey)} (${formatShortDate(start)} - ${formatShortDate(end)})`,
    };
  }, [schedules, weekOffset, i18n.language, t]);
  const hasData = data.some((point) => point.trips > 0);

  return (
    <div className="schedule-panel weekly-panel">
      <div className="schedule-panel-heading">
        <h3>{t("Weekly Trips Trend")}</h3>
        <select
          value={weekOffset}
          onChange={(event) => setWeekOffset(Number(event.target.value))}
        >
          <option value={0}>{label}</option>
          <option value={-1}>{t("Last Week")}</option>
          <option value={1}>{t("Next Week")}</option>
        </select>
      </div>
      <div className="schedule-chart">
        {!hasData ? (
          <div className="schedule-chart-empty">{t("No data")}</div>
        ) : (
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 8, right: 10, left: -12, bottom: 0 }}
            >
              <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 9 }}
                axisLine={false}
                label={{
                  value: t("Trips"),
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 9,
                }}
              />
              <Tooltip
                formatter={(value) => [value, t("Trips")]}
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.date || ""
                }
              />
              <Line
                type="monotone"
                dataKey="trips"
                name={t("Trips")}
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function Analytics({ schedules }) {
  return (
    <div className="schedule-analytics-grid">
      <TripsStartedByHour schedules={schedules} />
      <TripsStatusChart schedules={schedules} />
      <WeeklyTripsTrend schedules={schedules} />
    </div>
  );
}

const STATUS_META = {
  COMPLETED: {
    key: "COMPLETED",
    label: "On Time",
    tone: "green",
    progressTone: "green",
  },
  RUNNING: {
    key: "RUNNING",
    label: "On Time",
    tone: "green",
    progressTone: "green",
  },
  DELAYED: {
    key: "DELAYED",
    label: "Delayed",
    tone: "amber",
    progressTone: "amber",
  },
  CANCELLED: {
    key: "CANCELLED",
    label: "Cancelled",
    tone: "red",
    progressTone: "gray",
  },
  SCHEDULED: {
    key: "SCHEDULED",
    label: "Upcoming",
    tone: "gray",
    progressTone: "gray",
  },
};

const resolveTripStatus = (source, start, end, now) => {
  const scheduleStatus = String(
    source.scheduleStatus || source.status || "",
  ).toUpperCase();
  const delayed =
    scheduleStatus === "DELAYED" ||
    source.delayed === true ||
    source.isDelayed === true;
  if (scheduleStatus === "CANCELLED") return STATUS_META.CANCELLED;
  if (delayed) return STATUS_META.DELAYED;
  if (end && end <= now) return STATUS_META.COMPLETED;
  if (start && start <= now) return STATUS_META.RUNNING;
  return STATUS_META.SCHEDULED;
};

const getTripProgress = (start, end, statusKey, now = new Date()) => {
  if (statusKey === "CANCELLED" || !start || now < start) return 0;
  if (!end || now >= end) return 100;
  const duration = end.getTime() - start.getTime();
  if (duration <= 0) return 0;
  return Math.min(
    100,
    Math.max(
      0,
      Math.round(((now.getTime() - start.getTime()) / duration) * 100),
    ),
  );
};

const DEFAULT_TRIP_COLUMNS = [
  { key: "vehicle", label: "Vehicle", width: "12%" },
  { key: "route", label: "Route", width: "12%" },
  { key: "origin", label: "Origin", width: "12%" },
  { key: "destination", label: "Destination", width: "12%" },
  { key: "tripStart", label: "Start Time", width: "14%" },
  { key: "tripEnd", label: "End Time", width: "14%" },
  { key: "stops", label: "Stops", width: "6%" },
  { key: "tripStatus", label: "Status", width: "9%" },
  { key: "progress", label: "Progress", width: "9%" },
];

const formatTripDateTime = (date, locale = "en-US") => {
  if (!date) return "--";
  const weekday = date.toLocaleDateString(locale, { weekday: "short" });
  const day = date.toLocaleDateString(locale, { day: "2-digit" });
  const month = date.toLocaleDateString(locale, { month: "short" });
  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${weekday}, ${day} ${month}, ${time}`.toUpperCase();
};

function TripProgress({ value, tone }) {
  return (
    <div className="schedule-trip-progress">
      <span>{value}%</span>
      <i>
        <b className={tone} style={{ width: `${value}%` }} />
      </i>
    </div>
  );
}

const getStopLabel = (entry, stopNames) => {
  if (!entry) return "";
  const stop =
    entry.stop && typeof entry.stop === "object" ? entry.stop : entry;
  const stopId = entry.stopId || stop.id;
  return (
    stop.name ||
    entry.stopName ||
    stopNames?.[stopId] ||
    stopNames?.[String(stopId)] ||
    ""
  );
};

const getAssignedTripVehicles = (source, scheduleRow) => {
  const vehicles = [];
  const seen = new Set();
  const addVehicle = (vehicle) => {
    const id = vehicle?.id ?? vehicle?.vehicleId;
    const name = String(vehicle?.name || vehicle?.vehicleName || "").trim();
    const vehicleNumber = String(
      vehicle?.VehicleNumber ||
        vehicle?.vehicleNumber ||
        vehicle?.vehicle_number ||
        "",
    ).trim();
    const key =
      id != null && id !== "" ? `id:${id}` : name ? `name:${name}` : "";
    if (!key || seen.has(key)) return;
    seen.add(key);
    vehicles.push({
      id: id ?? key,
      name: name || "Unassigned",
      VehicleNumber: vehicleNumber || "--",
    });
  };

  const summaries = Array.isArray(source.vehicleScheduleSummary)
    ? source.vehicleScheduleSummary
    : [];
  const summaryById = new Map(
    summaries
      .filter((summary) => summary?.vehicleId != null)
      .map((summary) => [Number(summary.vehicleId), summary]),
  );

  if (Array.isArray(source.vehicleSchedule)) {
    source.vehicleSchedule.forEach((assignment) => {
      const nested = assignment?.vehicle;
      const vehicleId = assignment?.vehicleId ?? nested?.id;
      const summary = summaryById.get(Number(vehicleId));
      if (!nested && vehicleId == null && !summary) return;
      addVehicle({
        id: vehicleId,
        name: nested?.name || summary?.vehicleName,
        VehicleNumber:
          nested?.VehicleNumber ||
          nested?.vehicleNumber ||
          summary?.vehicleNumber,
      });
    });
  }

  summaries.forEach((summary) => {
    addVehicle({
      id: summary.vehicleId,
      name: summary.vehicleName,
      VehicleNumber: summary.vehicleNumber,
    });
  });

  if (!vehicles.length) {
    const names = String(scheduleRow.vehicle || "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value && value !== "N/A");
    const numbers = String(scheduleRow.vehicleNumber || "")
      .split(",")
      .map((value) => value.trim());
    names.forEach((name, index) => {
      addVehicle({
        id: `schedule-${scheduleRow.id}-vehicle-${index}`,
        name,
        VehicleNumber: numbers[index],
      });
    });
  }

  return vehicles;
};

const getScheduleEndpoints = (source, stopNames) => {
  const routeStops = source.route?.stops || source.route?.routeStops || [];
  const timedStops = [...(source.stopSchedules || [])]
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = new Date(
        left.arrivalTime || left.departureTime || 0,
      ).getTime();
      const rightTime = new Date(
        right.arrivalTime || right.departureTime || 0,
      ).getTime();
      return leftTime - rightTime;
    });
  const firstRouteStop = routeStops[0]?.stop || routeStops[0];
  const lastRouteStop =
    routeStops[routeStops.length - 1]?.stop ||
    routeStops[routeStops.length - 1];
  return {
    origin:
      getStopLabel(timedStops[0], stopNames) ||
      firstRouteStop?.name ||
      source.origin?.name ||
      "--",
    destination:
      getStopLabel(timedStops[timedStops.length - 1], stopNames) ||
      lastRouteStop?.name ||
      source.destination?.name ||
      "--",
    stopCount: timedStops.length || routeStops.length || 0,
  };
};

function TripsTable({ schedules, columns, stopNames = {} }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [route, setRoute] = React.useState("");
  const [datePeriod, setDatePeriod] = React.useState("today");
  const [selectedTripIds, setSelectedTripIds] = React.useState([]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = addDays(today, -1);
  const tomorrow = addDays(today, 1);
  const weekStart = startOfWeek(today);
  const weekEnd = addDays(weekStart, 6);
  const rows = useMemo(() => {
    const rangeStart = addDays(today, -7);
    const weekAwareStart = weekStart < rangeStart ? weekStart : rangeStart;
    const rangeEnd = addDays(weekAwareStart, 21);
    rangeEnd.setHours(23, 59, 59, 999);
    const weekAwareEnd = weekEnd > rangeEnd ? weekEnd : rangeEnd;

    const days = [];
    for (
      let cursor = new Date(weekAwareStart);
      cursor <= weekAwareEnd;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      days.push(toDateInputValue(cursor));
    }

    return schedules.flatMap((schedule) => {
      const source = schedule.original || {};
      const endpoints = getScheduleEndpoints(source, stopNames);
      const tripVehicles = getAssignedTripVehicles(source, schedule);
      if (!tripVehicles.length) return [];

      return days.flatMap((day) => {
        const occurrences = getScheduleOccurrencesForDate(source, day);
        if (!occurrences.length) return [];
        const now = new Date();

        return occurrences.flatMap((occurrence, occurrenceIndex) => {
          const occurrenceStart = validDate(occurrence.start);
          const occurrenceEnd = validDate(occurrence.end);
          const status = resolveTripStatus(
            source,
            occurrenceStart,
            occurrenceEnd,
            now,
          );

          return tripVehicles.map((vehicle) => ({
            ...schedule,
            id: `${schedule.id}-${vehicle.id}-${day}-${occurrenceIndex}`,
            vehicle: vehicle.name || "Unassigned",
            vehicleNumber: vehicle.VehicleNumber || "--",
            occurrenceDate: day,
            occurrenceLabel:
              occurrenceStart?.toLocaleDateString(localeFor(language), {
                month: "short",
                day: "numeric",
                weekday: "short",
              }) || "--",
            origin: endpoints.origin,
            destination: endpoints.destination,
            tripDate:
              occurrenceStart?.toLocaleDateString(localeFor(language), {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }) || "--",
            tripStart: formatTripDateTime(occurrenceStart, localeFor(language)),
            tripEnd: formatTripDateTime(occurrenceEnd, localeFor(language)),
            tripStartMs: occurrenceStart?.getTime() || 0,
            stops: endpoints.stopCount || "--",
            tripStatus: status.label,
            tripTone: status.tone,
            progressTone: status.progressTone,
            progress: getTripProgress(
              occurrenceStart,
              occurrenceEnd,
              status.key,
              now,
            ),
          }));
        });
      });
    });
  }, [schedules, stopNames, language, today, weekStart, weekEnd]);
  const matchesPeriod = (row) => {
    if (datePeriod === "all") return true;
    if (datePeriod === "today")
      return row.occurrenceDate === toDateInputValue(today);
    if (datePeriod === "yesterday")
      return row.occurrenceDate === toDateInputValue(yesterday);
    if (datePeriod === "tomorrow")
      return row.occurrenceDate === toDateInputValue(tomorrow);
    if (datePeriod === "thisWeek")
      return (
        row.occurrenceDate >= toDateInputValue(weekStart) &&
        row.occurrenceDate <= toDateInputValue(weekEnd)
      );
    return true;
  };
  const filteredRows = rows
    .filter((row) => (!route || row.route === route) && matchesPeriod(row))
    .sort((left, right) => left.tripStartMs - right.tripStartMs);
  const routes = [
    "",
    ...new Set(
      rows.map((row) => row.route).filter((value) => value && value !== "N/A"),
    ),
  ];
  const configuredColumns =
    Array.isArray(columns) && columns.length ? columns : DEFAULT_TRIP_COLUMNS;
  const headers = configuredColumns.map((column) => {
    if (column.key === "tripStatus")
      return {
        ...column,
        render: (row) => (
          <TableStatusBadge tone={row.tripTone}>
            {t(row.tripStatus)}
          </TableStatusBadge>
        ),
      };
    if (column.key === "progress")
      return {
        ...column,
        render: (row) => (
          <TripProgress
            value={row.progress}
            tone={row.progressTone || row.tripTone}
          />
        ),
      };
    if (column.key === "origin" || column.key === "destination") {
      return {
        ...column,
        render: (row) => (
          <span className="schedule-trip-place" title={row[column.key]}>
            {row[column.key] || "--"}
          </span>
        ),
      };
    }
    if (column.key === "vehicle")
      return {
        ...column,
        render: (row) => (
          <span className="schedule-trip-vehicle">
            <strong>{labelOrText(t, row.vehicle)}</strong>
            <small>{row.vehicleNumber || "--"}</small>
          </span>
        ),
      };
    return column;
  });

  const parsedUserState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "null");
    } catch {
      return null;
    }
  }, []);
  const orgName = parsedUserState?.organization?.name;
  const cleanOrgName = (orgName || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
  const tripExportFileName = `${cleanOrgName}_Trips`;

  return (
    <DynamicTable
      className="schedule-trips-table"
      title="Trips"
      headers={headers}
      data={filteredRows}
      downloadData={filteredRows}
      downloadFileName={tripExportFileName}
      selectable={true}
      selectedRowKeys={selectedTripIds}
      onSelectionChange={setSelectedTripIds}
      searchPlaceholder="Search vehicles"
      minWidth={1180}
      pagination={{ mode: "showMore", pageSize: 10, itemLabel: "trips" }}
      filters={[
        {
          key: "datePeriod",
          label: "Today",
          value: datePeriod,
          onChange: setDatePeriod,
          options: [
            { value: "today", label: "Today" },
            { value: "yesterday", label: "Yesterday" },
            { value: "tomorrow", label: "Tomorrow" },
            { value: "thisWeek", label: "This Week" },
            { value: "all", label: "All Dates" },
          ],
        },
        {
          key: "route",
          label: "All Routes",
          value: route,
          onChange: setRoute,
          options: routes.map((value) => ({
            value,
            label: value || "All Routes",
          })),
        },
      ]}
    />
  );
}

export default function ScheduleDashboardSections({
  sections,
  schedules,
  tripColumns = [],
  stopNames = {},
}) {
  const enabled = new Set(sections.map((section) => section.type));
  return (
    <>
      <div className="schedule-middle-grid">
        {enabled.has("timeline") && <Timeline schedules={schedules} />}{" "}
        {enabled.has("upcoming") && <Upcoming schedules={schedules} />}
      </div>
      {(enabled.has("hourlyChart") ||
        enabled.has("statusChart") ||
        enabled.has("weeklyChart")) && <Analytics schedules={schedules} />}
      {enabled.has("tripsTable") && (
        <TripsTable
          schedules={schedules}
          columns={tripColumns}
          stopNames={stopNames}
        />
      )}
    </>
  );
}
